// The composition behind the number: what the chosen clinician's week is made
// of, and whose values priced it (EMR-294, layer 3).

import {
  formatDateForDisplay,
  formatWeekdayAndDate,
} from '../../utils/dateUtils';
import { formatPointsValue, pluralizeTasks } from './formatPoints';
import type {
  ClinicianLoad,
  SchedulingPointLoadWeek,
} from '../../services/api/types.gen';

export interface ClinicianLoadDetailProps {
  week: SchedulingPointLoadWeek | undefined;
  load: ClinicianLoad | undefined;
}

/**
 * Who to credit for the values behind this number.
 *
 * Read from the **clinician**, not the week. Pooling this across the cohort —
 * as an earlier version did — meant that if any *other* candidate's week
 * touched a clinic-overridden activity, this clinician's agency-priced number
 * was captioned "On this clinic's values — set by Jane Doe". A scheduler who
 * thought the number wrong would go and edit Jane's value and watch nothing
 * move, because this week was never priced there.
 *
 * A person is named for one source and one only. The backend already nulls
 * `values_set_by` for the agency and global tiers, and there is deliberately no
 * fallback here: attributing the organization's standard or Roger's suggestion
 * to a named individual would make a real person answerable for a number they
 * never chose, and that is a harder thing to unpick than a missing line.
 *
 * `unpriced` renders nothing at all — including for a clinician carrying
 * nothing at all, where there is no priced work to have a provenance. "On this
 * clinic's values" under `0 · no tasks` is a claim about a number that does not
 * exist.
 */
const provenanceLine = (load: ClinicianLoad): string | null => {
  switch (load.values_source) {
    case 'clinic': {
      if (!load.values_set_by) return "On this clinic's values";
      const on = load.values_effective_from
        ? `, ${formatDateForDisplay(load.values_effective_from)}`
        : '';
      return `On this clinic's values — set by ${load.values_set_by}${on}`;
    }
    case 'agency':
      return "On your organization's standard values";
    case 'global':
      return "On Roger's suggested values";
    case 'unpriced':
      return null;
  }
};

/**
 * Four muted lines under the assignee field, for the clinician now selected.
 *
 * Shown only for the one person who has been chosen, and only in the Add Task
 * dialog: this is the composition of a number the scheduler has just acted on,
 * not a comparison aid. Beside a list of candidates it would be noise; beneath
 * a decision it is the answer to "wait, why is she at 31.5?".
 *
 * Every line is conditional on having something true to say. The block gets
 * shorter when the week is simpler, rather than padding to a fixed height with
 * zeros and dashes.
 */
export const ClinicianLoadDetail = ({
  week,
  load,
}: ClinicianLoadDetailProps) => {
  if (!week || !load) return null;

  const provenance = provenanceLine(load);
  const heaviest = load.top_types
    .map(type => `${type.display_name} ${formatPointsValue(type.points)}`)
    .join(' · ');

  return (
    // tabular-nums on the block rather than on each figure: the lines stack,
    // and the numbers only line up with each other if they share the advance
    // width down the whole column.
    <div className='mt-1.5 space-y-0.5 text-xs tabular-nums text-muted-foreground'>
      <div>
        Week of {formatDateForDisplay(week.week_start)} ·{' '}
        {formatPointsValue(load.points)} {week.unit_label} across{' '}
        {pluralizeTasks(load.task_count)}
      </div>

      {load.top_types.length > 0 && (
        <div>
          Heaviest: {heaviest}
          {load.other_type_count > 0 && ` +${load.other_type_count} more`}
        </div>
      )}

      {/* The day this task lands on, called out of the week around it — a
          clinician at a comfortable weekly total can still have an impossible
          Tuesday. Omitted when the day is empty, which the weekly line has
          already covered.

          Carries the unit the weekly line above carries. Two figures stacked
          one above the other, one of them named and one bare, invite the reader
          to take the bare one for something else — a task count, a fraction. */}
      {load.target_day_task_count > 0 && (
        <div>
          {formatWeekdayAndDate(week.target_date)} (this task&rsquo;s day):{' '}
          {formatPointsValue(load.target_day_points)} {week.unit_label} across{' '}
          {pluralizeTasks(load.target_day_task_count)}
        </div>
      )}

      {load.uncounted_task_count > 0 && (
        <div>
          {pluralizeTasks(load.uncounted_task_count)} this week aren&rsquo;t
          priced yet.
        </div>
      )}

      {/* Stated against the *counted* tasks, which is the set it is a subset
          of. Cross-clinic work is repriced at this clinic's values and lands in
          task_count; an unpriced cross-clinic task is only ever in
          uncounted_task_count. Hanging this off the unpriced line instead would
          make a false subset claim, and would also go silent whenever this
          clinician's borrowed work happens to all be priced. */}
      {load.other_clinic_task_count > 0 && (
        <div>
          {load.other_clinic_task_count} of those {load.task_count} tasks are at
          another clinic.
        </div>
      )}

      {provenance && <div>{provenance}</div>}
    </div>
  );
};

export default ClinicianLoadDetail;
